import { BookOpen, Users, Award, Target, Globe, Heart, TrendingUp, Lightbulb } from "lucide-react"
import Card, { CardContent, CardHeader, CardTitle } from "../components/Card"
import { useTranslation } from "../i18n"
import { useNavigate } from "react-router-dom"
import { motion, useInView } from "framer-motion"
import { useEffect, useMemo, useRef, useState } from "react"

function formatCount(value, { suffix = "", compact = false }) {
  if (compact) {
    // 10000 -> 10K
    const k = value / 1000
    const rounded = k >= 10 ? Math.round(k) : Math.round(k * 10) / 10
    return `${rounded}K${suffix}`
  }
  return `${Math.round(value)}${suffix}`
}

function getTargetFromStat(statNumber) {
  // Accepts: "10K+", "500+", "50+", "95%"
  const trimmed = String(statNumber || "").trim()
  if (!trimmed) return { target: 0, formatter: (v) => String(v) }

  const hasPercent = trimmed.endsWith("%")
  const hasK = /k/i.test(trimmed)
  const hasPlus = trimmed.includes("+")

  if (hasK) {
    const num = parseFloat(trimmed.replace(/[^0-9.]/g, "")) || 0
    const target = num * 1000
    return {
      target,
      formatter: (v) => formatCount(v, { suffix: hasPlus ? "+" : "", compact: true })
    }
  }

  if (hasPercent) {
    const num = parseFloat(trimmed.replace(/[^0-9.]/g, "")) || 0
    return {
      target: num,
      formatter: (v) => formatCount(v, { suffix: "%", compact: false })
    }
  }

  const num = parseFloat(trimmed.replace(/[^0-9.]/g, "")) || 0
  return {
    target: num,
    formatter: (v) => formatCount(v, { suffix: hasPlus ? "+" : "", compact: false })
  }
}

function CountUp({ value, durationMs = 1200, className = "" }) {
  const { target, formatter } = useMemo(() => getTargetFromStat(value), [value])
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: "-20% 0px" })
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!inView) return
    const start = performance.now()
    const from = 0
    const to = target

    let raf = 0
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs)
      // easing (easeOutCubic)
      const eased = 1 - Math.pow(1 - t, 3)
      setCurrent(from + (to - from) * eased)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, target, durationMs])

  return (
    <span ref={ref} className={className}>
      {formatter(current)}
    </span>
  )
}

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  )
}

export default function AboutPage() {
  const { tObj } = useTranslation()
  const navigate = useNavigate()

  const stats = [
    { number: "10K+", label: tObj.about.activeStudents(), icon: Users },
    { number: "500+", label: tObj.about.availableCourses(), icon: BookOpen },
    { number: "50+", label: tObj.about.expertTrainers(), icon: Award },
    { number: "95%", label: tObj.about.satisfactionRate(), icon: TrendingUp }
  ]

  const values = [
    {
      icon: Target,
      title: tObj.about.academicExcellence(),
      description: tObj.about.academicExcellenceDesc()
    },
    {
      icon: Heart,
      title: tObj.about.accessibilityForAll(),
      description: tObj.about.accessibilityForAllDesc()
    },
    {
      icon: Globe,
      title: tObj.about.techInnovation(),
      description: tObj.about.techInnovationDesc()
    },
    {
      icon: Lightbulb,
      title: tObj.about.practicalLearning(),
      description: tObj.about.practicalLearningDesc()
    }
  ]

  const team = [
    {
      name: "Mr. Brou Emmanuel",
      role: "Administrateur de la plateforme",
      description: "Expert en gestion de plateforme éducative et administration système",
      image: ""
    },
    {
      name: "Mr Lionel Abrogoua",
      role: "Développeur Full-Stack",
      description: "Ingénieur informatique spécialisé en développement web et mobile",
      image: ""
    },
    {
      name: "Mr.Franck Gozé",
      role: "Fondateur / Directeur Exécutif",
      description: "Visionnaire et leader passionné par l'éducation numérique",
      image: ""
    },
    {
      name: "Mr.Ravier Ankon",
      role: "Responsable Communication et Marketing",
      description: "Expert en communication digitale et stratégie de contenu",
      image: ""
    },
    {
      name: "Boris Koffi",
      role: "Directeur des Programmes",
      description: "Expert en pédagogie et innovation éducative",
      image: ""
    }
  ]

  const heroLines = useMemo(() => {
    // Animate "line by line": we split subtitle into 2–3 chunks
    const subtitle = String(tObj.about.subtitle?.() || "")
    const parts = subtitle.split(/(?<=[.!?])\s+/).filter(Boolean)
    if (parts.length <= 1) return [subtitle]
    // Keep it readable: max 3 lines
    if (parts.length === 2) return parts
    return [parts.slice(0, 2).join(" "), parts.slice(2).join(" ")]
  }, [tObj])

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
  }
  const rise = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  }

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      {/* Header */}
      <div className="pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-28 lg:pb-20 border-b border-[#D5DAE3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#203a8a] mb-6">
              {tObj.about.title()}
            </h1>
            <div className="font-sans text-lg sm:text-xl text-[#6B7280] max-w-3xl mx-auto leading-relaxed space-y-2">
              {heroLines.map((line, idx) => (
                <p key={idx} className="text-[#6B7280]">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          <Reveal>
          <div className="bg-white border border-[#D5DAE3] p-8 h-full flex flex-col">
            <h2 className="font-display text-3xl lg:text-4xl text-[#203a8a] mb-6">
              {tObj.about.ourStory()}
            </h2>
            <div className="space-y-4 font-sans text-[#6B7280] leading-relaxed flex-grow">
              <p>
                <strong className="text-[#1B2240]">AY Solutions Academy</strong> {tObj.about.ourStoryText1()}
              </p>
              <p>
                {tObj.about.ourStoryText2()}
              </p>
              <p>
                {tObj.about.ourStoryText3()}
              </p>
            </div>
          </div>
          </Reveal>
          <Reveal delay={0.08}>
          <div className="bg-[#203a8a] p-8 text-white">
            <h3 className="font-display text-4xl mb-4 text-white">{tObj.about.ourVision()}</h3>
            <p className="font-sans text-lg leading-relaxed text-white mb-6">
              {tObj.about.ourVisionText1()}
            </p>
            <p className="font-sans text-base leading-relaxed text-white/90">
              {tObj.about.ourVisionText2()}
            </p>
          </div>
          </Reveal>
        </div>
      </div>

      {/* Stats & Objectives */}
      <div className="bg-white py-16 border-t border-[#D5DAE3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* Stats */}
          <Reveal>
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="font-display text-3xl sm:text-4xl text-[#203a8a] mb-4">
                {tObj.about.statsTitle()}
              </h2>
              <p className="font-sans text-lg text-[#6B7280]">{tObj.about.statsSubtitle()}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {stats.map((stat, index) => {
                const Icon = stat.icon
                return (
                  <div key={index} className="text-center p-6 bg-[#FAFBFD] border border-[#D5DAE3]">
                    <div className="w-12 h-12 bg-[#203a8a] flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="font-display text-3xl sm:text-4xl text-[#203a8a] mb-2">
                      <CountUp value={stat.number} />
                    </div>
                    <div className="font-sans text-[#6B7280] text-xs sm:text-sm uppercase tracking-[0.22em]">{stat.label}</div>
                  </div>
                )
              })}
            </div>
          </div>
          </Reveal>
 
          {/* Objectives */}
          <Reveal delay={0.06}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl text-[#203a8a] mb-3">{tObj.about.goals2025()}</h2>
              <p className="font-sans text-[#6B7280] max-w-2xl mx-auto">
                {tObj.about.goalsSubtitle()}
              </p>
            </div>
            
            {/* Timeline Container */}
            <div className="relative">
              {/* Vertical Line - Hidden on mobile */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-[#D5DAE3]"></div>
              
              {/* Timeline Items */}
              <div className="space-y-12">
                {[
                  { title: tObj.about.goal1Title(), desc: tObj.about.goal1Desc(), num: "01" },
                  { title: tObj.about.goal2Title(), desc: tObj.about.goal2Desc(), num: "02" },
                  { title: tObj.about.goal3Title(), desc: tObj.about.goal3Desc(), num: "03" },
                  { title: tObj.about.goal4Title(), desc: tObj.about.goal4Desc(), num: "04" },
                  { title: tObj.about.goal5Title(), desc: tObj.about.goal5Desc(), num: "05" }
                ].map((goal, idx) => (
                  <Reveal key={idx} delay={idx * 0.08}>
                    <div className={`relative flex flex-col md:flex-row gap-8 items-center ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      {/* Content Card */}
                      <div className="w-full md:w-5/12">
                        <div className="bg-white border border-[#D5DAE3] border-l-4 border-l-[#203a8a] p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-14 h-14 bg-[#203a8a] flex items-center justify-center text-white font-bold text-xl">
                              {goal.num}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-display text-xl text-[#1B2240] mb-2">
                                {goal.title}
                              </h3>
                              <p className="font-sans text-[#6B7280] leading-relaxed">
                                {goal.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Center Dot */}
                      <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-white border-4 border-[#203a8a]">
                        <div className="absolute inset-1 bg-[#203a8a]"></div>
                      </div>
                      
                      {/* Spacer for alternating layout */}
                      <div className="hidden md:block w-5/12"></div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
          </Reveal>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-[#FAFBFD] py-16 border-t border-[#D5DAE3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl text-[#203a8a] mb-4">
              {tObj.about.valuesTitle()}
            </h2>
            <p className="font-sans text-lg text-[#6B7280] max-w-2xl mx-auto">
              {tObj.about.valuesSubtitle()}
            </p>
          </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              return (
                <Reveal key={index} delay={index * 0.04}>
                <div className="bg-white border border-[#D5DAE3] p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-[#203a8a] flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl text-[#1B2240] mb-2">
                        {value.title}
                      </h3>
                      <p className="font-sans text-[#6B7280] leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
                </Reveal>
              )
            })}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-white py-16 border-t border-[#D5DAE3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl text-[#203a8a] mb-4">{tObj.about.teamTitle()}</h2>
            <p className="font-sans text-[#6B7280] max-w-2xl mx-auto">
              {tObj.about.teamSubtitle()}
            </p>
          </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {team.map((member, index) => (
              <Reveal key={index} delay={index * 0.04}>
              <div className="bg-[#FAFBFD] border border-[#D5DAE3] p-4 sm:p-5 md:p-6 text-center">
                <div className="text-4xl sm:text-5xl md:text-6xl mb-3 sm:mb-4">{member.image}</div>
                <h3 className="font-display text-lg sm:text-xl text-[#1B2240] mb-1">{member.name}</h3>
                <p className="font-sans text-[#10px] uppercase tracking-[0.22em] text-[#203a8a] mb-2 text-sm sm:text-base">{member.role}</p>
                <p className="font-sans text-xs sm:text-sm text-[#6B7280]">{member.description}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-[#203a8a] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-4">
            {tObj.about.joinMission()}
          </h2>
          <p className="font-sans text-lg text-white/90 mb-8">
            {tObj.about.joinMissionText()}
          </p>
          </Reveal>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/courses')}
              className="bg-[#E8C547] text-[#203a8a] px-8 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#d4af3a] transition-colors"
            >
              {tObj.about.discoverCourses()}
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border border-white text-white px-8 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-[#203a8a] transition-colors"
            >
              {tObj.about.contactUs()}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
